// @spec SHL-STR-002
// @spec SHL-STR-051
// @spec POL-STR-001
// @spec POL-STR-030
// Navigation configuration for sidebar navigation

import type { LucideIcon } from 'lucide-react';
import {
  AlertTriangle,
  BarChart3,
  BookOpen,
  Bot,
  Building,
  Building2,
  Calendar,
  CalendarCog,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  Cloud,
  Database,
  FileText,
  FileWarning,
  GraduationCap,
  LayoutDashboard,
  Leaf,
  LifeBuoy,
  ListTodo,
  Megaphone,
  Send,
  Server,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  Scale,
  Sparkles,
  Target,
  User,
  Users,
} from 'lucide-react';

import i18n from '@/lib/i18n';
import { findBestNavigationMatchKey } from '@/lib/navigation-match';

/**
 * Badge configuration for navigation items.
 */
export interface NavigationBadge {
  count?: number;
  variant?: 'default' | 'secondary' | 'destructive';
}

/**
 * Single navigation item configuration.
 */
export interface NavigationItem {
  id: string;
  to: string;
  label: string;
  icon: LucideIcon;
  badge?: NavigationBadge;
  permission?: string;
  exact?: boolean;
  /** Module-specific icon color class */
  moduleColor?: string;
}

/**
 * Navigation group (category) configuration.
 */
export interface NavigationGroup {
  id: string;
  label: string;
  icon?: LucideIcon;
  items: NavigationItem[];
  defaultOpen?: boolean;
}

export interface NavigationItemWithGroup extends NavigationItem {
  groupId: string;
}

/**
 * Navigation configuration for the sidebar.
 *
 * Structured into 10 enterprise-focused groups.
 * Existing route paths are preserved.
 *
 * Returns a fresh config on each call so that `i18n.t()` labels
 * reflect the active language at render time.
 */
export function getNavigationConfig(): NavigationGroup[] {
  const t = i18n.t.bind(i18n) as (key: string) => string;

  return [
    {
      id: 'home',
      label: t('common:nav.dashboard'),
      icon: LayoutDashboard,
      defaultOpen: true,
      items: [
        {
          id: 'home-dashboard',
          to: '/dashboard',
          label: t('common:nav.dashboard'),
          icon: LayoutDashboard,
          exact: true,
        },
        {
          id: 'analytics-overview',
          to: '/analytics',
          label: t('common:nav.analytics'),
          icon: BarChart3,
        },
        {
          id: 'ai-advisor',
          to: '/ai',
          label: t('common:nav.aiAdvisor'),
          icon: Bot,
        },
        {
          id: 'reports',
          to: '/analytics/report-studio',
          label: t('common:nav.reports'),
          icon: FileText,
        },
      ],
    },
    {
      id: 'data',
      label: t('common:nav.data'),
      icon: Database,
      items: [
        {
          id: 'data-hub',
          to: '/datahub',
          label: t('common:nav.dataHub'),
          icon: Database,
        },
        {
          id: 'data-entry',
          to: '/datahub/upload',
          label: t('common:nav.dataEntry'),
          icon: FileText,
        },
        {
          id: 'data-my-entries',
          to: '/datahub/my-entries',
          label: t('common:nav.myEntries'),
          icon: CheckSquare,
        },
      ],
    },
    {
      id: 'task-management',
      label: t('common:nav.taskManagement'),
      icon: ClipboardCheck,
      items: [
        {
          id: 'my-tasks',
          to: '/tasks/my',
          label: t('common:nav.myTasks'),
          icon: CheckSquare,
        },
        {
          id: 'project-management',
          to: '/tasks/console',
          label: t('common:nav.taskProgress'),
          icon: ListTodo,
        },
        {
          id: 'compliance-playbooks',
          to: '/tasks/playbooks',
          label: t('common:nav.workflows'),
          icon: BookOpen,
        },
      ],
    },
    {
      id: 'training-awareness',
      label: t('common:nav.trainingAndAwareness'),
      icon: GraduationCap,
      items: [
        {
          id: 'workforce-readiness',
          to: '/workforce-readiness/training-register',
          label: t('common:nav.trainingRegister'),
          icon: GraduationCap,
        },
        {
          id: 'my-trainings',
          to: '/workforce-readiness/my-trainings',
          label: t('common:nav.myTrainings'),
          icon: CheckSquare,
        },
      ],
    },
    {
      id: 'surveys',
      label: t('common:nav.surveys'),
      icon: Send,
      items: [
        {
          id: 'surveys-templates',
          to: '/surveys',
          label: t('common:nav.surveyTemplates'),
          icon: ClipboardList,
        },
        {
          id: 'surveys-campaigns',
          to: '/surveys/campaigns',
          label: t('common:nav.campaigns'),
          icon: Megaphone,
        },
        {
          id: 'surveys-schedules',
          to: '/surveys/schedules',
          label: t('common:nav.schedules'),
          icon: Calendar,
        },
      ],
    },
    {
      id: 'risk-security',
      label: t('common:nav.riskAndSecurity'),
      icon: AlertTriangle,
      items: [
        {
          id: 'assets',
          to: '/assets',
          label: t('common:nav.assetInventory'),
          icon: Server,
        },
        {
          id: 'vulnerabilities',
          to: '/vulnerabilities',
          label: t('common:nav.vulnerabilities'),
          icon: ShieldAlert,
        },
        {
          id: 'threats',
          to: '/threats',
          label: t('common:nav.threats'),
          icon: ShieldAlert,
        },
        {
          id: 'risks',
          to: '/risks',
          label: t('common:nav.riskRegister'),
          icon: AlertTriangle,
        },
        {
          id: 'incidents',
          to: '/incidents',
          label: t('common:nav.incidents'),
          icon: FileWarning,
        },
        {
          id: 'controls',
          to: '/controls',
          label: t('common:nav.controls'),
          icon: BookOpen,
        },
        {
          id: 'business-impact-analysis',
          to: '/bcp/bia',
          label: t('common:nav.bia'),
          icon: FileText,
        },
        {
          id: 'external-reporting',
          to: '/external-reporting',
          label: t('common:nav.externalReporting'),
          icon: Shield,
        },
      ],
    },
    {
      id: 'vendors',
      label: t('common:nav.vendors'),
      icon: Building2,
      items: [
        {
          id: 'supplier-register',
          to: '/suppliers/register',
          label: t('common:nav.supplierRegister'),
          icon: Building,
        },
      ],
    },
    {
      id: 'policies',
      label: t('common:nav.policies'),
      icon: BookOpen,
      items: [
        {
          id: 'policies-library',
          to: '/policies',
          label: t('common:nav.policiesLibrary'),
          icon: BookOpen,
        },
        {
          id: 'policies-documents',
          to: '/policies/documents',
          label: t('common:nav.policiesDocuments'),
          icon: FileText,
        },
        {
          id: 'procedures',
          to: '/procedures',
          label: t('common:nav.procedures'),
          icon: FileText,
        },
        {
          id: 'policy-generator',
          to: '/policies/wizard',
          label: t('common:nav.policyGenerator'),
          icon: Sparkles,
        },
      ],
    },
    {
      id: 'business-continuity',
      label: t('common:nav.businessContinuity'),
      icon: LifeBuoy,
      items: [
        {
          id: 'business-impact-analysis',
          to: '/bcp/bia',
          label: t('common:nav.bia'),
          icon: FileText,
        },
        {
          id: 'business-continuity-plan',
          to: '/bcp/bcp',
          label: t('common:nav.bcp'),
          icon: ShieldCheck,
        },
        {
          id: 'bcp-dashboard',
          to: '/bcp/dashboard',
          label: t('common:nav.bcpDashboard'),
          icon: LifeBuoy,
        },
        {
          id: 'bcp-plans',
          to: '/bcp/plans?section=all-plans',
          label: t('common:nav.allPlans'),
          icon: FileText,
        },
        {
          id: 'continuity-plans',
          to: '/bcp/plans?section=incident_response_plan',
          label: t('common:nav.continuityPlans'),
          icon: FileText,
        },
        {
          id: 'bcp-exercises',
          to: '/bcp/exercises',
          label: t('common:nav.exercises'),
          icon: CheckSquare,
        },
      ],
    },
    {
      id: 'audit',
      label: t('common:nav.audit'),
      icon: ClipboardCheck,
      items: [
        {
          id: 'audit-dashboard',
          to: '/audit',
          label: t('common:nav.auditDashboard'),
          icon: LayoutDashboard,
        },
        {
          id: 'audit-audits',
          to: '/audit/audits',
          label: t('common:nav.audits'),
          icon: ClipboardCheck,
        },
        {
          id: 'audit-findings',
          to: '/audit/findings',
          label: t('common:nav.findings'),
          icon: FileWarning,
        },
        {
          id: 'audit-evidence',
          to: '/evidence/repository',
          label: t('common:nav.evidence'),
          icon: Database,
        },
        {
          id: 'audit-calendar',
          to: '/audit/calendar',
          label: t('common:nav.auditCalendar'),
          icon: Calendar,
        },
        {
          id: 'audit-reports',
          to: '/audit/reports',
          label: t('common:nav.auditReports'),
          icon: FileText,
        },
        {
          id: 'audit-documents',
          to: '/datahub/documents',
          label: t('common:nav.datahubDocuments'),
          icon: BookOpen,
        },
      ],
    },
    {
      id: 'frameworks',
      label: t('common:nav.frameworks'),
      icon: Shield,
      defaultOpen: true,
      items: [
        {
          id: 'ghg',
          to: '/ghg/overview',
          label: t('common:nav.ghgEmissions'),
          icon: Cloud,
          moduleColor: 'text-emerald-500',
        },
        {
          id: 'nis2',
          to: '/nis2/dashboard',
          label: t('common:nav.nis2'),
          icon: Shield,
          moduleColor: 'text-accent-500',
        },
        {
          id: 'esg',
          to: '/esg/overview',
          label: 'ESG / CSRD',
          icon: Leaf,
          moduleColor: 'text-sky-600',
        },
        {
          id: 'eu-taxonomy',
          to: '/eu-taxonomy/overview',
          label: 'EU Taxonomy',
          icon: Scale,
          moduleColor: 'text-indigo-600',
        },
        {
          id: 'vsme',
          to: '/vsme/overview',
          label: 'VSME',
          icon: ClipboardList,
          moduleColor: 'text-teal-600',
        },
        {
          id: 'framework-mappings',
          to: '/settings/compliance-frameworks',
          label: t('common:nav.frameworkMappings'),
          icon: Target,
        },
      ],
    },
    {
      id: 'settings',
      label: t('common:nav.settings'),
      icon: Settings,
      items: [
        {
          id: 'configration',
          to: '/settings/configration',
          label: t('common:nav.configurations'),
          icon: Server,
          permission: 'org:settings:read',
        },
        {
          id: 'profile',
          to: '/settings/profile',
          label: t('common:nav.profile'),
          icon: User,
        },
        {
          id: 'employee-hub',
          to: '/employees',
          label: t('common:nav.employeeHub'),
          icon: Users,
          permission: 'org:members:read',
        },
        {
          id: 'organisation-structure',
          to: '/settings/organisation-structure',
          label: t('common:nav.organisationStructure'),
          icon: Building2,
          permission: 'org:settings:read',
        },
        {
          id: 'reporting-periods',
          to: '/settings/reporting-periods',
          label: t('common:nav.reportingPeriods'),
          icon: CalendarCog,
          permission: 'org:settings:read',
        },
        {
          id: 'security',
          to: '/settings/security',
          label: t('common:nav.security'),
          icon: Shield,
        },
        {
          id: 'appearance',
          to: '/settings/appearance',
          label: t('common:nav.appearance'),
          icon: Settings,
        },
        {
          id: 'admin-settings',
          to: '/admin/settings',
          label: t('common:nav.adminSettings'),
          icon: Settings,
          permission: 'platform_admin',
        },
      ],
    },
  ];
}

/** @deprecated Use getNavigationConfig() for i18n support */
export const navigationConfig: NavigationGroup[] = getNavigationConfig();


export function flattenNavigationItems(groups: NavigationGroup[]): NavigationItemWithGroup[] {
  return groups.flatMap((group) =>
    group.items.map((item) => ({
      ...item,
      groupId: group.id,
    }))
  );
}

export function findActiveNavigationItemId(
  groups: NavigationGroup[],
  currentPath: string,
  currentHash?: string
): string | null {
  const items = flattenNavigationItems(groups);

  return findBestNavigationMatchKey({
    items,
    currentPath,
    currentHash,
    getKey: (item) => item.id,
    getTarget: (item) => item.to,
    getExact: (item) => item.exact ?? false,
  });
}
